/**
 * Pure boot helpers for game controller — session testSpeed from URL only.
 * Persisted settings.testSpeed is ignored / sanitized false.
 */
import {
  loadGameState,
  reduceGame,
  type GameState,
  type StorageAdapter,
} from "../../game/index.js";
import {
  resolvePackIdFromQuery,
  type PackId,
} from "../../content/registry.js";
import { CHALLENGE_QUERY_KEY } from "../../game/challenge/url.js";
import {
  tryParseChallengeFromSearch,
  tryParsePresentationFromSearch,
} from "../../game/challenge/url.js";
import type { GameAction } from "../../game/reducer.js";

export function readTestSpeedFromSearch(search: string): boolean {
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  );
  return params.get("testSpeed") === "1" || params.get("testspeed") === "1";
}

/** testSpeed-only cash seed (`?seedCash=50`) — ignored unless testSpeed query is on. */
export function readSeedCashFromSearch(search: string, testSpeed: boolean): number | null {
  if (!testSpeed) return null;
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  );
  const raw = params.get("seedCash") ?? params.get("seedcash");
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.min(9999, Math.floor(n));
}

function readQaPack(search: string): PackId | null {
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  );
  if (!params.has("scenario") && !params.has("pack")) return null;
  if (params.has(CHALLENGE_QUERY_KEY)) return null;
  return resolvePackIdFromQuery(search.startsWith("?") ? search : `?${search}`);
}

function bootChallengeAction(search: string): GameAction | null {
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  );
  if (!params.has(CHALLENGE_QUERY_KEY)) return null;
  const parsed = tryParseChallengeFromSearch(search);
  if (!parsed.ok) {
    return { type: "OPEN_CHALLENGE_INVALID", reason: parsed.reason };
  }
  const ch = parsed.challenge;
  // Game-side presentation sidecar (`?p=`). A malformed sidecar degrades to no
  // levers rather than blocking the challenge (which the core `?c=` governs).
  const sidecar = tryParsePresentationFromSearch(search);
  const challengerPresentation = sidecar.ok ? sidecar.presentation : null;
  return {
    type: "OPEN_CHALLENGE",
    packId: ch.sourcePackId as PackId,
    packVersion: ch.sourcePackVersion,
    seed: ch.seed,
    constraintIds: ch.constraintIds,
    challengerGraphJson: JSON.stringify(ch.graph),
    ...(challengerPresentation ? { challengerPresentation } : {}),
    challengerScore: {
      reach: ch.score.reach,
      credDelta: ch.score.credDelta,
      contextLoss: ch.score.contextLoss,
      claimDrift: ch.score.claimDrift,
      provenanceRisk: ch.score.provenanceRisk,
    },
  };
}

/**
 * Boot campaign state. `testSpeed` comes only from the current search string.
 * Seed cash is applied in-memory only when the query requests it.
 */
export function bootGameControllerState(
  adapter: StorageAdapter,
  search: string,
): { state: GameState; recovered: boolean; reason?: string } {
  const loaded = loadGameState(adapter);
  let state = loaded.state;
  // Always session-only — never rehydrate persisted testSpeed.
  const testSpeed = readTestSpeedFromSearch(search);
  state = reduceGame(state, { type: "SET_TEST_SPEED", enabled: testSpeed });
  const seedCash = readSeedCashFromSearch(search, testSpeed);
  if (seedCash != null && state.economy.cash < seedCash) {
    state = {
      ...state,
      economy: { ...state.economy, cash: seedCash },
    };
  }
  const challengeAction = bootChallengeAction(search);
  if (challengeAction) {
    state = reduceGame(state, challengeAction);
  } else {
    const qa = readQaPack(search);
    if (qa) {
      state = reduceGame(state, { type: "QA_OPEN_EDITOR", packId: qa });
    }
  }
  return { state, recovered: loaded.recovered, reason: loaded.reason };
}

/** Test-speed sessions must not write campaign economy/purchases to storage. */
export function shouldPersistGameState(state: GameState): boolean {
  return !state.settings.testSpeed;
}
